import RefreshIcon from "@mui/icons-material/Refresh";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const REFRESH_TRIGGER_DISTANCE = 132;
const MAX_PULL_DISTANCE = 180;
const PULL_RESISTANCE = 0.45;
const PULL_START_DEADZONE = 28;
const MAX_VISIBLE_PULL_DISTANCE = MAX_PULL_DISTANCE - PULL_START_DEADZONE;
const MIN_CLOSE_ANIMATION_MS = 840;
const MAX_CLOSE_ANIMATION_MS = 1500;
const SCROLL_TOP_EPSILON = 1;
const RELEASE_REST_DISTANCE = 68;

/**
 * Finds the nearest ancestor that owns vertical scrolling for the touch target.
 * Pull-to-refresh should only intercept gestures when this container is already
 * at the top; otherwise the browser needs the gesture to scroll content upward.
 */
function getScrollableParent(target: EventTarget | null): HTMLElement | null {
  let node: HTMLElement | null = null;

  if (target instanceof HTMLElement) {
    node = target;
  } else if (target instanceof Element) {
    node = target.parentElement;
  }

  while (node) {
    const style = window.getComputedStyle(node);
    const allowsScroll =
      style.overflowY === "auto" || style.overflowY === "scroll";
    if (allowsScroll && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Treats tiny scroll offsets as "at top" because mobile browsers can expose
 * fractional scroll positions during momentum/bounce. This keeps the refresh
 * gesture from feeling randomly disabled near the top edge.
 */
function isScrollElementAtTop(element: HTMLElement | null): boolean {
  if (!element) {
    return isDocumentAtTop();
  }
  return element.scrollTop <= SCROLL_TOP_EPSILON;
}

/**
 * Checks the document scroll position separately from nested scroll containers.
 * Some pages have an inner container at scrollTop=0 while the document itself is
 * scrolled down; in that case we must not intercept the drag.
 */
function isDocumentAtTop(): boolean {
  const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY;
  return scrollTop <= SCROLL_TOP_EPSILON;
}

/**
 * Keeps the first few pixels of downward movement invisible so normal taps and
 * tiny scroll corrections do not flash the refresh card.
 */
function getVisiblePullDistance(distance: number): number {
  return Math.max(0, distance - PULL_START_DEADZONE);
}

/**
 * Converts pull distance into close speed. A stronger pull closes faster, which
 * makes the release animation feel tied to the amount of spring force the user
 * built up before releasing.
 */
function getCloseAnimationDuration(distance: number): number {
  const pullForce = Math.min(
    1,
    Math.max(0, distance / MAX_VISIBLE_PULL_DISTANCE),
  );
  return Math.round(
    MAX_CLOSE_ANIMATION_MS -
      (MAX_CLOSE_ANIMATION_MS - MIN_CLOSE_ANIMATION_MS) * pullForce,
  );
}

/**
 * Fades card content based on current displacement from the top. This stays
 * reversible while dragging, so backing off after the threshold also fades the
 * text/icon back out.
 */
function getPullContentOpacity(distance: number): number {
  return Math.min(1, Math.max(0, distance / MAX_VISIBLE_PULL_DISTANCE));
}

export function MobilePullToRefresh() {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isReadyToRefresh, setIsReadyToRefresh] = useState(false);
  const [isReloadLockedIn, setIsReloadLockedIn] = useState(false);
  const [closeAnimationMs, setCloseAnimationMs] = useState(
    MAX_CLOSE_ANIMATION_MS,
  );

  const pullDistanceRef = useRef<number>(0);
  const reloadTimeoutRef = useRef<number | undefined>(undefined);
  const isReadyToRefreshRef = useRef<boolean>(false);

  /**
   * Keeps native touch listeners in sync with React readiness state. The
   * listeners are registered once, so reading state directly from them would use
   * a stale value from the initial render.
   */
  useEffect(() => {
    isReadyToRefreshRef.current = isReadyToRefresh;
  }, [isReadyToRefresh]);

  /**
   * Mirrors the latest pull distance for release-time calculations. The touch
   * end handler needs the final distance to choose the close animation speed,
   * but it also runs from the stable native listener closure.
   */
  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  const overlayVisible = pullDistance > 0 || isReloadLockedIn;

  /**
   * Moves the app root down under the pull card. This uses imperative styles
   * because the transformed node lives outside this component; React only owns
   * the overlay portal.
   */
  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) {
      return;
    }

    if (!overlayVisible) {
      root.style.transform = "";
      root.style.transition = "";
      root.style.willChange = "";
      return;
    }

    root.style.willChange = "transform";
    root.style.transition = isPulling
      ? "none"
      : `transform ${closeAnimationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`;
    root.style.transform = `translate3d(0, ${pullDistance}px, 0)`;
  }, [closeAnimationMs, isPulling, overlayVisible, pullDistance]);

  /**
   * Final safety cleanup for timers and root styles. This covers route/app
   * unmounts where the normal gesture reset path may not run.
   */
  useEffect(() => {
    return () => {
      if (reloadTimeoutRef.current) {
        window.clearTimeout(reloadTimeoutRef.current);
      }

      const root = document.getElementById("root");
      if (!root) {
        return;
      }
      root.style.transform = "";
      root.style.transition = "";
      root.style.willChange = "";
    };
  }, []);

  /**
   * Hides native page scrolling while the pull UI is active. Without this, the
   * browser can keep rubber-banding the document underneath the custom refresh
   * card, which makes the drag distance and visual offset disagree.
   */
  useEffect(() => {
    if (!overlayVisible) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousDocumentOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;
    };
  }, [overlayVisible]);

  /**
   * Owns the native touch lifecycle for coarse pointers. The listener is kept
   * stable so we do not re-register global handlers on every drag frame; mutable
   * local variables and refs carry per-gesture state instead.
   */
  useEffect(() => {
    const coarsePointer = window.matchMedia("(pointer: coarse)");
    if (!coarsePointer.matches) {
      return;
    }

    let touchStartY = 0;
    let isPullTracking = false;
    let activeScrollElement: HTMLElement | null = null;
    let lastVisiblePullDistance = 0;
    let peakPullDistance = 0;
    let needsPeakRecovery = false;

    /**
     * Removes imperative transform styles that were applied to the app root.
     * The component owns these styles while active, so cleanup has to restore
     * the root even if the gesture is cancelled before React re-renders.
     */
    const clearRootTransform = () => {
      const root = document.getElementById("root");
      if (root) {
        root.style.transform = "";
        root.style.transition = "";
        root.style.willChange = "";
      }
    };

    /**
     * Clears all per-gesture tracking state. This does not clear root transform
     * by itself because locked reload uses the same reset path while React keeps
     * the release animation mounted.
     */
    const clearPullTracking = () => {
      setIsPulling(false);
      setPullDistance(0);
      setIsReadyToRefresh(false);
      touchStartY = 0;
      peakPullDistance = 0;
      lastVisiblePullDistance = 0;
      isPullTracking = false;
      needsPeakRecovery = false;
      activeScrollElement = null;
    };

    /**
     * Fully cancels the interaction and returns the page to normal. This path is
     * used for invalid gestures, scroll position changes, touch cancellation,
     * and non-refresh releases.
     */
    const resetPullState = () => {
      if (reloadTimeoutRef.current) {
        window.clearTimeout(reloadTimeoutRef.current);
        reloadTimeoutRef.current = undefined;
      }
      setIsReloadLockedIn(false);
      clearPullTracking();
      clearRootTransform();
    };

    /**
     * Locks in the refresh after release. The UI is kept mounted with
     * isReloadLockedIn while pullDistance animates back to the configured rest
     * distance, then the page reload starts partway through the close animation.
     */
    const triggerRefresh = () => {
      const animationDuration = getCloseAnimationDuration(
        pullDistanceRef.current,
      );
      setCloseAnimationMs(animationDuration);
      setIsReloadLockedIn(true);
      clearPullTracking();
      setPullDistance(RELEASE_REST_DISTANCE);
      reloadTimeoutRef.current = window.setTimeout(() => {
        window.location.reload();
      }, animationDuration / 2);
    };

    /**
     * Starts tracking only for one-finger downward gestures that begin at the
     * top of both the document and active scroll container. If either can still
     * scroll upward, native scrolling must win.
     */
    const onTouchStart = (ev: TouchEvent) => {
      if (ev.touches.length !== 1) {
        resetPullState();
        return;
      }

      activeScrollElement = getScrollableParent(ev.target);
      if (!isDocumentAtTop() || !isScrollElementAtTop(activeScrollElement)) {
        resetPullState();
        return;
      }

      touchStartY = ev.touches[0]?.clientY || 0;
      isPullTracking = true;
      lastVisiblePullDistance = 0;
      peakPullDistance = 0;
      needsPeakRecovery = false;
      setIsPulling(true);
      setIsReadyToRefresh(false);
    };

    /**
     * Converts touch movement into pull distance, applies resistance, and
     * updates refresh readiness. If the user crosses the threshold then drags
     * back upward, readiness is cancelled until they recover the previous peak
     * distance to avoid duplicate/overlapping status text.
     */
    const onTouchMove = (ev: TouchEvent) => {
      if (!isPullTracking || ev.touches.length !== 1) {
        return;
      }

      const currentY = ev.touches[0]?.clientY || 0;
      const rawDeltaY = currentY - touchStartY;
      if (rawDeltaY <= 0) {
        lastVisiblePullDistance = 0;
        peakPullDistance = 0;
        needsPeakRecovery = false;
        setPullDistance(0);
        setIsReadyToRefresh(false);
        return;
      }

      if (!isDocumentAtTop() || !isScrollElementAtTop(activeScrollElement)) {
        resetPullState();
        return;
      }

      ev.preventDefault();
      const dampedDelta = Math.min(
        MAX_PULL_DISTANCE,
        rawDeltaY * PULL_RESISTANCE,
      );
      const visiblePullDistance = getVisiblePullDistance(dampedDelta);
      const isMovingBackUp = visiblePullDistance < lastVisiblePullDistance;
      peakPullDistance = Math.max(peakPullDistance, visiblePullDistance);

      if (isMovingBackUp && isReadyToRefreshRef.current) {
        needsPeakRecovery = true;
      }

      const hasRecoveredPeak =
        !needsPeakRecovery || visiblePullDistance >= peakPullDistance;
      const nextIsReady =
        visiblePullDistance >= REFRESH_TRIGGER_DISTANCE && hasRecoveredPeak;

      if (nextIsReady) {
        needsPeakRecovery = false;
      }

      lastVisiblePullDistance = visiblePullDistance;
      pullDistanceRef.current = visiblePullDistance;
      setPullDistance(visiblePullDistance);
      setIsReadyToRefresh(nextIsReady);
    };

    /**
     * Finishes the gesture. Release only triggers reload while current readiness
     * is true; otherwise the card and translated root snap back to rest.
     */
    const onTouchEnd = () => {
      if (!isPullTracking) {
        resetPullState();
        return;
      }

      if (isReadyToRefreshRef.current) {
        triggerRefresh();
      } else {
        resetPullState();
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  if (!overlayVisible) {
    return null;
  }

  const revealDistance = Math.min(REFRESH_TRIGGER_DISTANCE, pullDistance);
  const panelTranslateY = revealDistance - REFRESH_TRIGGER_DISTANCE;
  const contentOpacity = isReloadLockedIn
    ? 1
    : getPullContentOpacity(pullDistance);
  const showRefreshIcon = isReadyToRefresh || isReloadLockedIn;

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[1300]">
      <div
        className="mx-auto w-full max-w-[460px] overflow-hidden border-x border-b border-slate-800 bg-[var(--color-primary--darker)]"
        style={{
          height: `${REFRESH_TRIGGER_DISTANCE}px`,
          boxShadow: "inset 0 -12px 18px -18px rgb(255 255 255 / 0.35)",
          transform: `translate3d(0, ${panelTranslateY}px, 0)`,
          transition: isPulling
            ? "none"
            : `transform ${closeAnimationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        }}
      >
        <div
          className="flex h-full flex-col items-center justify-end px-4 pb-4 opacity-0 transition-opacity duration-300 ease-out"
          style={{ opacity: contentOpacity }}
        >
          <p className="m-0 flex flex-col items-center justify-center gap-1 text-center text-sm font-medium text-slate-100">
            {showRefreshIcon && (
              <RefreshIcon
                className={isReloadLockedIn ? "animate-spin" : ""}
                fontSize="small"
              />
            )}
            {isReloadLockedIn
              ? "Refreshing..."
              : isReadyToRefresh
                ? "Release to refresh"
                : "Pull to refresh"}
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
